import {
  type DimTuple,
  decimalsFor,
  dimText,
  pivot,
  type ReportModel,
  type ReportSpec,
  reportSpecSchema,
  tupleKey,
} from '@clockwise/semantic-core';
import { ApiError } from '@clockwise/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Info, TriangleAlert } from 'lucide-react';
import { Fragment, useMemo } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useElementWidth } from '@/hooks/use-element-width';
import { analyticsApi } from '@/lib/api';
import { isForbidden } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';

/**
 * THE REPORT'S OWN MODEL, named here rather than re-derived.
 *
 * `budgets` is the id `budgetsModel` registers under in the API's analytics
 * registry. Everything below asks the SAME endpoint the Budget vs Actuals
 * report asks, with the same measures, so the figures on this tab and the
 * figures on that report are one computation and cannot drift apart.
 */
const MODEL_ID = 'budgets';

/** Measure ids on that model. `overUnderHours` and `overUnderPct` are BOTH
 *  rendered, stacked in the (Over)/Under column: the hours are what has to be
 *  recovered or re-agreed, and the percentage is what makes roles comparable.
 *  Neither is decorative — dropping either empties half that column.
 *
 *  THE ACTUAL COLUMN IS `billableActualHours`, because `(Over)/Under` is
 *  Budget − BILLABLE actual on the report. Printing `actualHours` beside it
 *  made the row fail its own arithmetic: 10 budget, 16 actual, −4.00 over,
 *  where the −4 came from 14 billable hours nobody could see. `actualHours`
 *  is still asked for, but only to say what the column leaves out — the
 *  non-billable remainder — and to keep a row whose hours are all
 *  non-billable from being dropped as empty. */
const MEASURES = [
  'budgetHours',
  'billableActualHours',
  'actualHours',
  'overUnderHours',
  'overUnderPct',
] as const;

/**
 * The dimensions down the side, and the one across the top.
 *
 * ENGAGEMENT LEADS THE ROW AXIS, because a budget belongs to a retainer
 * engagement — so "is this retainer over?" is the question this tab is opened
 * to answer, and a table grouped by role alone could not be read that way: a
 * customer with two retainers funding the same role showed one row whose
 * figures belonged to neither agreement.
 *
 * The role stays beneath it rather than being replaced. An allowance is
 * defined per role, so it is where the numbers actually live; the engagement
 * is the grouping a reader needs on top of them.
 */
const ROW_DIMS = ['engagement', 'serviceRole'] as const;
const COL_DIM = 'budgetPeriod';

/** Precision, taken from the report's own resolution rather than as a second
 *  opinion: hours print at 2dp and a percentage at 1dp on both surfaces. */
const HOURS_DP = decimalsFor('hours');
const PCT_DP = decimalsFor('percent');

/**
 * HOW FAR BACK THIS LOOKS, in whole months ending with the one in progress.
 *
 * A FIXED WINDOW AND NO DATE CONTROL, which is the whole point. This is a
 * glance on a customer page, not the report: the question it answers is "are
 * we over lately", and that question has the same answer every time it is
 * asked. A period picker here would be a second, differently-shaped copy of
 * the report's own — and the reader who genuinely wants last year, or one
 * specific quarter, wants the report, where every axis is theirs to choose.
 *
 * Six is the span that shows a trend without needing a scrollbar: on a monthly
 * budget it is six columns.
 */
const MONTHS_BACK = 6;

/**
 * Column widths in px, mirroring the Tailwind classes on the header cells —
 * Role (w-44), then Budget (w-20), Actual Billable (w-20) and (Over)/Under (w-24) for
 * every period.
 *
 * THE TABLE ADAPTS TO ITS CONTAINER AND NEVER SCROLLS SIDEWAYS. Six periods is
 * eighteen figure columns, which does not fit a customer page on most screens,
 * and the two usual answers are both bad: an auto-layout table SQUEEZES, so
 * `100.00` wraps and role names truncate until the whole thing is unreadable,
 * and a floored width SCROLLS, which hides half the answer behind a gesture
 * nobody makes on a table that looks complete.
 *
 * So the columns keep these widths — `table-fixed` — and the number of PERIODS
 * bends instead: as many as fit, newest first, and the footer says how many
 * were left out. A narrow window shows three months properly rather than six
 * illegibly.
 *
 * THEY ARE DECLARED IN A `<colgroup>`, and that is load-bearing rather than
 * stylistic. `table-fixed` takes each column's width from the FIRST row that
 * offers one, and this table's first header row covers every triplet with a
 * single `colSpan={3}` cell — so widths written on the second row's `<th>`s
 * are read by nobody. They were, and the figure columns silently shared the
 * leftover space instead: 107px apiece in a 926px container, and 61px in a
 * 420px one, where the numbers wrapped and the rows grew from 46px to 59px
 * tall. That is the squeeze this whole design exists to prevent, arrived at
 * from the other direction. A `<col>` is the one place `table-fixed` always
 * looks, so the numbers below are now the numbers on screen — which is also
 * what makes the fit arithmetic mean anything.
 */
const ROLE_COLUMN_PX = 176;
const BUDGET_COLUMN_PX = 80;
const ACTUAL_COLUMN_PX = 80;
const VARIANCE_COLUMN_PX = 96;
const PERIOD_COLUMNS_PX = BUDGET_COLUMN_PX + ACTUAL_COLUMN_PX + VARIANCE_COLUMN_PX;
/** Breathing room kept to the right of the last period, so the table never
 *  sits flush against its own border. */
const GUTTER_PX = 24;
/**
 * Everything left of the periods: one `ROLE_COLUMN_PX` column PER ROW
 * DIMENSION. Counting one while the `<colgroup>` drew two (Engagement + Role)
 * fitted one period too many and pushed the newest-but-oldest month off the
 * right edge — a table 1632px wide in a 1504px frame, cut off mid-column.
 */
const LABEL_COLUMNS_PX = ROLE_COLUMN_PX * ROW_DIMS.length;

/**
 * How many periods fit in `width`, newest first.
 *
 * At least one, always: a container too narrow for even one period gets one
 * anyway, because a table with a Role column and nothing beside it answers
 * nothing. Below that the cells narrow, which is the lesser evil at a width
 * no real layout reaches.
 *
 * `0` means NOT YET MEASURED — the first paint, before the observer fires —
 * and is deliberately treated as "show everything" rather than as "show one".
 * The alternative renders a single column and then widens, which reads as the
 * page correcting a mistake.
 */
export function periodsThatFit(width: number, available: number): number {
  if (width === 0) return available;
  const room = Math.floor((width - LABEL_COLUMNS_PX - GUTTER_PX) / PERIOD_COLUMNS_PX);
  return Math.max(1, Math.min(available, room));
}

interface Props {
  customerId: string;
}

/**
 * Budget vs Actuals for ONE customer — the Budgets tab's second view.
 *
 * WHY THIS IS NOT THE `/budgets/actuals` ENDPOINT. That endpoint computed its
 * own allowances, its own windows and its own variance, and its answers did
 * not agree with the Budget vs Actuals report's for the same customer and the
 * same month. Two implementations of one question is one of them being wrong
 * in a way nobody can see, so this view runs the REPORT'S query — same model,
 * same measures, same coverage rules, same SQL — and only lays the answer out
 * to suit a customer page. `docs/reporting/budget-vs-actuals.md` defines every
 * number below; there is no arithmetic here beyond rendering it.
 *
 * ONE TABLE, NOT A BLOCK PER BUDGET, and that follows from the same decision.
 * The report's row is a POOL — customer + service role + period occurrence —
 * and where two budgets fund one role over one period it resolves them to a
 * single governing allowance. There is no budget ROW left to hang a block on
 * without re-deciding that, which is the thing this view exists not to do.
 *
 * Roles down the side and periods across the top: role names get room, and
 * reading one role across the months is the comparison people come here for.
 * Two customers on different reset periods must never share this axis — see
 * the doc — but this view is one customer, so the only way `Jul 2026` and
 * `Q3 2026` appear together is that this customer holds both a monthly and a
 * quarterly budget, where they are distinct columns that are never added up.
 */
export function CustomerBudgetActualsPanel({ customerId }: Props) {
  // The catalog, for the ONE model this view runs. `pivot()` needs it to
  // rebuild a ratio's total from its two halves: `(Over)/Under %` over a
  // column is that column's total variance over its total allowance, and never
  // the sum — or the mean — of the percentages above it.
  const catalog = useQuery({
    queryKey: queryKeys.analytics.models(),
    queryFn: ({ signal }) => analyticsApi.models(signal).then((r) => r.data),
    // The vocabulary changes on deploy, not during a session. This is the same
    // cache entry the report builder fills, so opening this tab after a report
    // costs nothing.
    staleTime: Number.POSITIVE_INFINITY,
  });
  const model = (catalog.data as ReportModel[] | undefined)?.find((m) => m.id === MODEL_ID);

  // The window, and the spec over it.
  //
  // KEYED ON THE MONTH, not on the mount. `trailingWindow()` reads the clock,
  // so memoising it on `customerId` alone froze it: a page left open over a
  // month boundary went on reporting the six months it was opened with, and
  // nothing recovered it — a refetch rebuilds the same query key from the same
  // frozen spec, so the stale window came back. Keying on the current month
  // makes the boundary itself the thing that invalidates it, while still
  // rebuilding at most once a month rather than on every render.
  const month = currentMonthKey();
  const spec = useMemo(() => buildSpec(customerId, trailingWindow()), [customerId, month]);

  const result = useQuery({
    queryKey: queryKeys.customerBudgets.actualsReport(customerId, spec),
    queryFn: ({ signal }) => analyticsApi.query(spec, signal),
    placeholderData: keepPreviousData,
  });

  const view = useMemo(
    () => (model && result.data ? pivot(model, spec, result.data) : null),
    [model, spec, result.data],
  );

  /**
   * Newest period first, the way the rest of this app reads time.
   *
   * Sorted on the period's KEY, which leads with its start date as text, so a
   * lexical comparison is a chronological one — and `Jul 2026` and `Q3 2026`
   * stay in date order instead of being ranked by the words in their labels.
   * At a shared start the key's `/end` puts the longer period first here.
   */
  const colKeys = useMemo(
    () => [...(view?.colKeys ?? [])].sort((a, b) => periodKey(b).localeCompare(periodKey(a))),
    [view],
  );
  /**
   * Roles alphabetically, so one customer reads the same way on every load
   * whatever order the rows happened to come back in.
   *
   * Rows with NOTHING TO SAY are dropped: no allowance and no hours, in any
   * period on screen. The report emits a row per (engagement, role) that
   * appears anywhere in the window, so a role budgeted under one engagement
   * produces an all-zero row under every OTHER engagement — and the
   * unbudgeted group ("No engagement") produces one per role whether or not
   * anyone ever worked outside an agreement.
   *
   * A zero is not nothing: a row with 0 budget and real hours is unbudgeted
   * work and the most important row on the table, so the test is that EVERY
   * measure is absent or zero, across every visible period.
   */
  const rowKeys = useMemo(() => {
    if (!view) return [];
    const says = (rk: DimTuple) =>
      colKeys.some((ck) =>
        (['budgetHours', 'actualHours'] as const).some((m) => {
          const v = view.cell(rk, ck, m);
          return v != null && v !== 0;
        }),
      );
    return [...view.rowKeys]
      .filter(says)
      .sort((a, b) => dimText(a[0]).localeCompare(dimText(b[0])));
  }, [view, colKeys]);

  // As many periods as the container can show at full width, newest first.
  // The rest are not rendered at all rather than scrolled to — see the width
  // constants above for why neither squeezing nor scrolling was taken.
  const [frame, frameWidth] = useElementWidth<HTMLDivElement>();
  const shown = periodsThatFit(frameWidth, colKeys.length);
  const visibleCols = colKeys.slice(0, shown);
  const hidden = colKeys.length - visibleCols.length;

  // Billable hours are a SUBSET of all hours by construction (the model sums
  // `budget_consuming_hours` for both, the billable one behind a CASE). A cell
  // where billable exceeds all hours means the two measures have stopped
  // agreeing on what they count — never clamp that to "no split", say so.
  const inconsistent = view
    ? visibleCols.some((ck) =>
        rowKeys.some(
          (rk) =>
            nonBillableHours(
              view.cell(rk, ck, 'billableActualHours'),
              view.cell(rk, ck, 'actualHours'),
            ) < 0,
        ),
      )
    : false;

  const failure = catalog.error ?? result.error;

  return (
    <div className="space-y-3" ref={frame}>
      {catalog.isLoading || result.isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : failure ? (
        <div className="rounded-md border">
          {isForbidden(failure) ? (
            <p className="p-6 text-center text-sm text-muted-foreground">
              You don’t have access to reporting, which is where these figures come from.
            </p>
          ) : (
            <p className="p-4 text-sm text-destructive">
              {failure instanceof ApiError ? failure.message : 'Failed to load budget vs actuals.'}
            </p>
          )}
        </div>
      ) : !view || rowKeys.length === 0 ? (
        <div className="rounded-md border">
          <p className="p-6 text-center text-sm text-muted-foreground">
            No budget periods in the last {MONTHS_BACK} months. Check that a budget was in force, or
            use the Budget vs Actuals report for another span.
          </p>
        </div>
      ) : (
        <div className="rounded-md border">
          {/* No `minWidth`: the table takes the width it is given and the
              period count is what was fitted to it, so nothing here can start
              a horizontal scroll. */}
          <Table className="table-fixed">
            {/* WHERE `table-fixed` ACTUALLY READS WIDTHS — see the constants
                above. One `<col>` per rendered column, in order: one per row
                dimension, then Budget/Actual/(Over)/Under per period, then the
                spacer, which is left unsized so it absorbs whatever is spare.
                The label columns come from `ROW_DIMS`, the same source
                `LABEL_COLUMNS_PX` is computed from, so the fit arithmetic and
                the table it predicts cannot drift apart again. */}
            <colgroup>
              {ROW_DIMS.map((dim) => (
                <col key={dim} style={{ width: ROLE_COLUMN_PX }} />
              ))}
              {visibleCols.map((ck) => (
                <Fragment key={tupleKey(ck)}>
                  <col style={{ width: BUDGET_COLUMN_PX }} />
                  <col style={{ width: ACTUAL_COLUMN_PX }} />
                  <col style={{ width: VARIANCE_COLUMN_PX }} />
                </Fragment>
              ))}
              <col />
            </colgroup>
            <TableHeader>
              {/* TWO HEADER ROWS, because each period carries three figures and
                  a single row would either repeat the month three times or
                  leave the triplet unlabelled. The period spans its three; the
                  three name themselves underneath. */}
              <TableRow>
                <TableHead rowSpan={2} className="align-bottom">
                  Engagement
                </TableHead>
                <TableHead rowSpan={2} className="align-bottom">
                  Role
                </TableHead>
                {visibleCols.map((ck) => (
                  <TableHead
                    key={tupleKey(ck)}
                    colSpan={3}
                    className="border-l text-center"
                    scope="colgroup"
                  >
                    {dimText(ck[0])}
                  </TableHead>
                ))}
                {/* The only column without a width, so it soaks up the spare
                    space — that keeps the figures packed together instead of
                    drifting apart on a wide screen. */}
                <TableHead rowSpan={2} />
              </TableRow>
              <TableRow>
                {visibleCols.map((ck) => (
                  <Fragment key={tupleKey(ck)}>
                    {/* `border-l` on the FIRST of each triplet only, so the
                        rule falls between periods and never inside one.

                        THE UNIT SITS ON ITS OWN LINE, which is what lets all
                        three carry it. Inline, `Budget (hrs)` already wanted
                        83px of an 80px column and `(Over)/Under (hrs)` wanted
                        far more — and widening the triplet costs a whole
                        PERIOD, since `periodsThatFit` divides the frame by it.
                        A second header line costs a few pixels ONCE,
                        vertically, instead of per period. */}
                    <TableHead className="border-l text-right text-xs font-normal">
                      <MeasureHead label="Budget" unit="hrs" />
                    </TableHead>
                    <TableHead className="text-right text-xs font-normal">
                      <MeasureHead label="Actual Billable" unit="hrs" />
                    </TableHead>
                    <TableHead className="text-right text-xs font-normal">
                      {/* Both units, because the cell carries both figures. */}
                      <MeasureHead label="(Over)/Under" unit="hrs / %" />
                    </TableHead>
                  </Fragment>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rowKeys.map((rk) => (
                <TableRow key={tupleKey(rk)}>
                  {/* The engagement repeats down its roles rather than being
                      suppressed: this table is read a row at a time — "is this
                      retainer's Controller over?" — and a blank first cell
                      makes that question need the rows above it. */}
                  <TableCell className="truncate text-sm" title={dimText(rk[0])}>
                    {dimText(rk[0])}
                  </TableCell>
                  <TableCell className="truncate text-sm" title={dimText(rk[1])}>
                    {dimText(rk[1])}
                  </TableCell>
                  {visibleCols.map((ck) => (
                    <PeriodCells
                      key={tupleKey(ck)}
                      budget={view.cell(rk, ck, 'budgetHours')}
                      billable={view.cell(rk, ck, 'billableActualHours')}
                      actual={view.cell(rk, ck, 'actualHours')}
                      overUnder={view.cell(rk, ck, 'overUnderHours')}
                      pct={view.cell(rk, ck, 'overUnderPct')}
                    />
                  ))}
                  <TableCell />
                </TableRow>
              ))}
              {/* Tinted, so the eye finds the bottom line without reading up
                  from it. `bg-muted/50` rather than a border: the row already
                  carries weight from `bold`, and a rule under a table that
                  ends anyway is a line doing nothing. */}
              <TableRow className="bg-muted/50 hover:bg-muted/50">
                {/* Spans both row dimensions now that the axis is
                    (engagement, role) — a Total under one of them and a blank
                    under the other reads as a total OF that column. */}
                <TableCell className="text-sm font-medium" colSpan={ROW_DIMS.length}>
                  Total
                </TableCell>
                {visibleCols.map((ck) => (
                  /* Down the ROLES of one period. There is deliberately no
                     total ACROSS the periods: adding a monthly column to a
                     quarterly one, or twelve months of the same recurring
                     allowance, produces a figure nobody agreed to. */
                  <PeriodCells
                    key={tupleKey(ck)}
                    budget={view.colTotal(ck, 'budgetHours')}
                    billable={view.colTotal(ck, 'billableActualHours')}
                    actual={view.colTotal(ck, 'actualHours')}
                    overUnder={view.colTotal(ck, 'overUnderHours')}
                    pct={view.colTotal(ck, 'overUnderPct')}
                    bold
                  />
                ))}
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        </div>
      )}

      {inconsistent ? (
        <p className="text-xs text-destructive">
          Some Actual Billable figures exceed all hours booked for the same period, which should not
          be possible — check these against the Budget vs Actuals report.
        </p>
      ) : null}

      {/* NEVER SILENT about the periods that did not fit. A table showing
          three months when six were asked for looks complete, and a customer
          whose overspend was four months ago reads as fine. */}
      {hidden > 0 ? (
        <p className="text-xs text-muted-foreground">
          Showing the {visibleCols.length} most recent of {colKeys.length} budget periods — widen
          the window, or use the Budget vs Actuals report, to see the rest.
        </p>
      ) : null}

      {/* The report's row cap bit. Never silent: a short answer that looks
          complete is how a customer reads as under budget. */}
      {result.data?.meta.truncated ? (
        <p className="text-xs text-destructive">
          This answer reached the report’s row cap and is incomplete.
        </p>
      ) : null}
    </div>
  );
}

/** A measure's name with its unit beneath, so the unit never competes with the
 *  label for the column's width. */
function MeasureHead({ label, unit }: { label: string; unit: string }) {
  return (
    <>
      {/* `whitespace-normal` because `TableHead` is `nowrap`: "Actual
          Billable" wants 92px of an 80px column, and unwrapped it ran into
          the "(Over)/Under" header beside it. Wrapping costs a line of header
          height once; widening the column would cost a period. */}
      <span className="block whitespace-normal leading-tight">{label}</span>
      {/* A real space between the two, which the `block` spans swallow
          visually but the accessible name does not: without it the header
          announces as "Budgethrs". */}{' '}
      <span className="block leading-tight text-muted-foreground">({unit})</span>
    </>
  );
}

/**
 * One period's three cells for one row: the allowance, the BILLABLE hours
 * booked against it, and the signed variance as a share of that allowance —
 * so Budget − Actual Billable is the variance, on screen, every time.
 *
 * THREE COLUMNS AND NOT A STACKED CELL, matching the report's own shape. Two
 * numbers stacked in one cell need a header explaining which is which; three
 * columns label themselves, and a column of allowances is readable down its
 * length in a way a column of `12.70 / 35.00` never is.
 *
 * The variance column carries BOTH figures, stacked. `(Over)/Under %` is what
 * makes roles comparable — 5 hours over a 7-hour allowance and 2 hours over a
 * 1-hour one are −71% and −200%, and the hours alone rank those the wrong way
 * round — while the hours are the quantity that actually has to be recovered
 * or re-agreed, and a percentage on a small allowance overstates the work
 * involved. Neither stands in for the other, so neither is a tooltip. The
 * percentage is read off the SERVER'S measure rather than recomputed from the
 * two cells beside it, so it and they can never come to different conclusions
 * about a pool sitting exactly on its cap.
 *
 * Over is the NEGATIVE number — the accounting sign the report's own
 * `(Over)/Under` header states — and only that case is coloured.
 */
function PeriodCells({
  budget,
  billable,
  actual,
  overUnder,
  pct,
  bold = false,
}: {
  budget: number | null;
  /** The figure printed: the billable hours `overUnder` is computed from. */
  billable: number | null;
  /** All hours, billable and not. Never printed in the cell — only in the
   *  split behind the billable figure's icon. */
  actual: number | null;
  overUnder: number | null;
  pct: number | null;
  bold?: boolean;
}) {
  const weight = bold ? ' font-medium' : '';
  // `align-top` on all three, because the variance cell is TWO lines tall and
  // the others are one. Vertically centred — the table default — that made
  // the variance number float half a line above the budget and actual beside
  // it, so the three figures a reader compares were never on one line.
  const base = `align-top text-right text-sm tabular-nums${weight}`;
  // Neither an allowance nor an hour: this role has nothing to say about this
  // period. A dash, never a zero — unreported is not zero-against-an-allowance.
  if (budget == null && billable == null && actual == null) {
    return (
      <>
        <TableCell className={`${base} border-l text-muted-foreground`}>—</TableCell>
        <TableCell className={`${base} text-muted-foreground`}>—</TableCell>
        <TableCell className={`${base} text-muted-foreground`}>—</TableCell>
      </>
    );
  }

  const over = overUnder != null && overUnder < 0;
  const left = nonBillableHours(billable, actual);
  return (
    <>
      {/* BUDGET AND ACTUAL ARE UNCOLOURED, and that is the point of having a
          variance column. Hours booked are a fact, not a verdict: painting
          51.95 red says the work was wrong, when what is wrong is that it
          exceeded an allowance — which is the next cell along, and the only
          cell that can be over or under anything. It also makes the tone mean
          one thing, so a reader scanning for red finds the overruns rather
          than every figure that sits beside one. */}
      <TableCell className={`${base} border-l`}>{hours(budget)}</TableCell>
      {/* The figure stays plain; the split sits behind a small icon AFTER
          it, shown only when there IS a split, so the icon marks the cells
          worth hovering rather than every one.

          The icon is positioned off the figure's right edge rather than laid
          out beside it, so the figure sits exactly where an icon-less one
          does and the column stays aligned under its header. It lives INSIDE
          the cell's right padding — `p-3` is 12px, and the 2px gap plus the
          10px icon is exactly that — so it never crosses into the
          (Over)/Under cell and the fixed 80px column needs no extra room.

          A split is hidden until the CELL is hovered, so a column of figures
          reads clean; `focus-within` keeps it visible to a keyboard user
          tabbing onto it. A DISAGREEMENT is never hidden: the note under the
          table says one exists but not where, and this icon is how a reader
          finds the cell. */}
      <TableCell className={`${base} group/split`}>
        {left !== 0 ? (
          <span className="relative inline-block">
            {hours(billable ?? 0)}
            <span
              data-testid="hours-split-hint"
              className={`absolute top-1/2 left-full ml-0.5 flex -translate-y-1/2${
                left > 0
                  ? ' opacity-0 transition-opacity group-hover/split:opacity-100 focus-within:opacity-100'
                  : ''
              }`}
            >
              <HoursSplitHint billable={billable ?? 0} actual={actual ?? 0} left={left} />
            </span>
          </span>
        ) : (
          hours(billable)
        )}
      </TableCell>
      <TableCell className={`${base}${over ? ' text-destructive' : ''}`}>
        {/* BOTH FIGURES, because they answer different questions and neither
            stands in for the other. The hours are what has to be recovered or
            re-agreed; the percentage is what makes roles comparable — 5 hours
            over a 7-hour allowance and 2 over a 1-hour one are −71% and −200%,
            and the hours alone rank those the wrong way round. */}
        <span className="block leading-tight">{hours(overUnder)}</span>
        <span className={`block text-xs${over ? '' : ' text-muted-foreground'}`}>
          {percent(pct)}
        </span>
      </TableCell>
    </>
  );
}

/**
 * The icon after an Actual Billable figure that leaves hours out, and the
 * split on hover. The sentence is also the trigger's accessible name, so the
 * split is announced without anyone having to hover.
 *
 * A negative `left` is the disagreement `nonBillableHours` refuses to clamp:
 * it gets a warning icon rather than the info one, because it is not a split.
 */
function HoursSplitHint({
  billable,
  actual,
  left,
}: {
  billable: number;
  actual: number;
  left: number;
}) {
  const disagree = left < 0;
  const label = disagree
    ? `${hours(billable)} billable exceeds ${hours(actual)} total hours — these figures disagree`
    : `${hours(billable)} billable + ${hours(left)} non-billable = ${hours(actual)} total`;
  const Icon = disagree ? TriangleAlert : Info;
  // No provider of its own: the app root mounts one `TooltipProvider`, and
  // sharing it is what lets the pointer move icon to icon without re-waiting
  // the open delay at every cell.
  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          className={`inline-flex shrink-0 cursor-help rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
            disagree ? 'text-destructive' : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <Icon className="size-2.5" aria-hidden />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top">
        {disagree ? (
          label
        ) : (
          <dl className="grid grid-cols-[auto_auto] gap-x-3 tabular-nums">
            <dt>Billable</dt>
            <dd className="text-right">{hours(billable)}</dd>
            <dt>Non-billable</dt>
            <dd className="text-right">{hours(left)}</dd>
            <dt className="font-medium">Total</dt>
            <dd className="text-right font-medium">{hours(actual)}</dd>
          </dl>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * The non-billable hours: all actual hours less the billable ones, SIGNED.
 *
 * Rounded to the printed precision before it is compared, so float residue
 * from subtracting two sums (`16 − 14.000000001`) never reads as a split.
 *
 * NEGATIVE IS RETURNED, NOT CLAMPED. Billable hours can only exceed all hours
 * if the two measures disagree about what they count, and a clamp to zero
 * would render that as "no non-billable hours" — a plausible, wrong reading.
 * Callers treat `< 0` as the inconsistency it is.
 */
function nonBillableHours(billable: number | null, actual: number | null): number {
  return Number(((actual ?? 0) - (billable ?? 0)).toFixed(HOURS_DP));
}

/** Hours at the report's own precision. `—` for absent, never `0.00`. */
function hours(v: number | null): string {
  return v == null ? '—' : v.toFixed(HOURS_DP);
}

/**
 * A ratio as a percentage.
 *
 * The measure carries a RATIO — `0.637`, not `63.7` — which is the convention
 * every percent-formatted measure in the semantic layer uses; the ×100 happens
 * at the point of display, here and in the report, and nowhere in between.
 *
 * `—` when there is none, which is a real and frequent state: a role with
 * hours and no allowance has no denominator, and 0% would report that
 * unbudgeted work as having spent exactly what it was given.
 */
function percent(v: number | null): string {
  return v == null ? '—' : `${(v * 100).toFixed(PCT_DP)}%`;
}

/** A column tuple's period key — the occurrence's start date, as text. */
function periodKey(tuple: DimTuple): string {
  return tuple[0]?.key ?? '';
}

/**
 * The last {@link MONTHS_BACK} whole months, ending today.
 *
 * `to` is TODAY and never the end of the month, which is what keeps periods
 * that have not happened yet off this table. The report answers for a future
 * occurrence honestly — a December pool with a 50h allowance and no hours is
 * `0.00`, `50.00`, `100.0%`, every word of it true — and it reads at a glance
 * as a month that came in comfortably under budget. Nothing is prorated, so
 * there is no partial figure to show instead; the only fix is not to ask.
 *
 * The range picks WHICH occurrences appear — any that overlap it — and each is
 * then measured over its own full window, so the month in progress is
 * period-to-date rather than clipped at today.
 *
 * MONTHS, on a model whose axis is budget periods. A customer on a monthly
 * reset gets six columns; on a quarterly reset the same six months are two or
 * three quarters, and on an annual one a single year. That is the honest
 * reading — the axis belongs to the budget, not to the calendar — and it is
 * why this is stated as a span rather than as a column count.
 *
 * Built from local date parts via `Date.UTC`, never `new Date(iso)`: that
 * parses as UTC midnight and reports the previous day to anyone west of
 * Greenwich.
 */
function trailingWindow(): { from: string; to: string } {
  const now = new Date();
  const start = new Date(Date.UTC(now.getFullYear(), now.getMonth() - (MONTHS_BACK - 1), 1));
  return {
    from: iso(start),
    to: iso(new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()))),
  };
}

/** The month the caller is living in, `YYYY-MM`. What the window's memo is
 *  keyed on, so the window follows the calendar without being rebuilt on every
 *  render. */
function currentMonthKey(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function iso(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/**
 * The spec this view runs: the Budget vs Actuals report, filtered to one
 * customer.
 *
 * PARSED rather than written out in full, so every field the schema defaults
 * stays defaulted and this cannot fall behind a spec shape that grows a key.
 *
 * `period: 'custom'` because the window IS two dates this view computed, not a
 * macro the reader chose. A saved report stores a macro so it re-answers
 * itself later; this spec is rebuilt on every mount, so the dates are the
 * whole of the truth.
 */
function buildSpec(customerId: string, window: { from: string; to: string }): ReportSpec {
  return reportSpecSchema.parse({
    model: MODEL_ID,
    kind: 'pivot',
    period: 'custom',
    from: window.from,
    to: window.to,
    // The whole of the scoping, and what makes this one customer's tab rather
    // than the report: the `customer` dimension's key is the customer id.
    filters: [
      {
        id: 'customer',
        field: 'customer',
        op: 'in',
        value: { kind: 'list', values: [customerId] },
        paused: false,
      },
    ],
    measures: [...MEASURES],
    rows: [...ROW_DIMS],
    cols: [COL_DIM],
    // The total row is drawn from `pivot()` rather than from the server, but
    // the spec says so anyway — it is what the answer is being asked for.
    totals: { row: false, col: true },
  });
}
